import { Code, User, Search, Rocket, CalendarDays } from "lucide-react";

const SDECurriculum = () => {
  const curriculum = [
    {
      week: "Week 1",
      title: "Laying the Groundwork",
      icon: <Code className="w-5 h-5 text-blue-400" />,
      sessions: [
        {
          title: "Start Smart: Roadmap + DSA Strategy + CS Fundamentals",
          description:
            "No more confusion—just a clean, realistic plan to start your prep",
        },
        {
          title: "CGPA, Open Source, Hackathons, Publications, Projects",
          description:
            "Stand out without needing a 9.9 CGPA or 100 stars on GitHub",
        },
      ],
    },
    {
      week: "Week 2",
      title: "Build Your Brand",
      icon: <User className="w-5 h-5 text-indigo-400" />,
      sessions: [
        {
          title: "Resume Building: GitHub, Certs & First Internship Tricks",
          description:
            'What to write when you feel you have "nothing to write"',
        },
        {
          title: "LinkedIn + Portfolio Setup",
          description: "Let recruiters scroll → stop → message you",
        },
      ],
    },
    {
      week: "Week 3",
      title: "Outreach & Discovery",
      icon: <Search className="w-5 h-5 text-purple-400" />,
      sessions: [
        {
          title: "Finding Off-Campus Internships (beyond job boards)",
          description:
            "Use X-ray search, Discord servers, and sneaky alumni tactics",
        },
        {
          title: "Cold Mail Templates + Referral Framework",
          description:
            "Slide into inboxes like a pro—without sounding desperate",
        },
      ],
    },
    {
      week: "Week 4",
      title: "Final Prep & Launch",
      icon: <Rocket className="w-5 h-5 text-emerald-400" />,
      sessions: [
        {
          title: "Interview Ready: From OA to HR",
          description:
            "Crack the coding rounds, ace the tech interviews, impress HR",
        },
        {
          title: "What to Do, What Not to Do + AMA",
          description:
            "Red flags, green flags, and answers to all your burning questions",
        },
      ],
    },
  ];

  const techStack = ["Strategy", "Branding", "Outreach", "Interviews"];

  return (
    <div className="bg-black py-12 px-4 relative overflow-hidden">
      {/* Tech stack floating animation */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 text-blue-400/10 font-mono text-sm animate-pulse">
          {`function success() { return hardWork + smartStrategy; }`}
        </div>
        <div className="absolute bottom-20 right-10 text-green-400/10 font-mono text-sm animate-pulse">
          {`while(learning) { skills++; }`}
        </div>
      </div>

      <div className="max-w-4xl mx-auto relative z-10">
        <div className="flex items-center justify-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/30 backdrop-blur-sm">
            <CalendarDays className="w-4 h-4 text-blue-400" />
            <span className="text-sm font-semibold text-blue-400 tracking-wide uppercase">
              SDE Intern Fellowship Curriculum
            </span>
          </div>
        </div>

        <div className="space-y-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-gray-500 bg-clip-text text-transparent">
                4 Weeks | 8 Live Sessions
              </span>
            </h2>

            {/* Tech stack badges */}
            <div className="flex flex-wrap justify-center gap-2 mt-6 max-w-2xl mx-auto">
              {techStack.map((tech, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-gray-800/50 rounded-full text-xs font-mono text-gray-400 border border-gray-700 hover:border-blue-500/50 hover:text-blue-400 transition-all"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div className="relative space-y-8 py-2 px-2 md:px-0 before:absolute before:inset-0 before:left-[15px] md:before:left-1/2 before:ml-0.5 md:before:ml-0 before:w-0.5 before:bg-gradient-to-b before:from-blue-500 before:via-indigo-500 before:to-purple-500 before:opacity-20">
            {curriculum.map((week, index) => (
              <div
                key={index}
                className={`relative flex items-start ${
                  index % 2 === 0 ? "md:flex-row-reverse" : ""
                }`}
              >
                {/* Timeline dot with enhanced styling */}
                <div className="absolute left-0 md:left-1/2 transform -translate-x-1/2 w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 border-2 border-blue-400 z-10 flex items-center justify-center shadow-lg shadow-blue-500/30">
                  {week.icon}
                </div>

                {/* Content card with enhanced styling */}
                <div
                  className={`ml-16 md:ml-0 ${
                    index % 2 === 0
                      ? "md:mr-12 md:pr-16 md:text-right"
                      : "md:ml-12 md:pl-16"
                  } 
                         w-full md:w-2/5 group relative bg-gradient-to-br from-gray-900/50 to-black border border-blue-500/30 rounded-xl p-6 
                         hover:border-blue-400 transition-all duration-300 
                         hover:bg-gradient-to-br hover:from-blue-900/20 hover:to-indigo-900/20
                         hover:shadow-lg hover:shadow-blue-500/10`}
                >
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl blur opacity-0 group-hover:opacity-20 transition duration-300" />

                  <div className="relative">
                    <div className="flex items-center justify-between mb-3">
                      <div className="font-bold text-blue-400 text-sm bg-blue-500/10 px-3 py-1 rounded-full border border-blue-500/30">
                        {week.week}
                      </div>
                      <div className="px-2 py-1 bg-gray-800/50 rounded text-xs font-mono text-gray-400 border border-gray-700">
                        2 sessions
                      </div>
                    </div>

                    <h3 className="text-xl font-bold mb-4 text-white group-hover:text-blue-300 transition-colors">
                      {week.title}
                    </h3>

                    <div className="space-y-4">
                      {week.sessions.map((session, sessionIndex) => (
                        <div key={sessionIndex} className="relative">
                          <div className="border-l-2 border-blue-500/30 pl-4 hover:border-blue-400/50 transition-colors">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                              <h4 className="font-semibold text-white text-sm">
                                Session {sessionIndex + 1}:
                              </h4>
                            </div>
                            <h5 className="font-bold text-blue-300 mb-2 text-sm">
                              {session.title}
                            </h5>
                            <p className="text-gray-400 text-sm group-hover:text-gray-200 transition-colors leading-relaxed">
                              {session.description}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call to Action */}
          <div className="mt-12 text-center">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-black to-blue-950 border border-blue-500/20 p-6 md:p-8 mb-8">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-32 -mt-32" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -ml-32 -mb-32" />

              {/* Code snippet background */}
              <div className="absolute inset-0 flex items-center justify-center opacity-5">
                <div className="font-mono text-blue-400/30 text-sm">{`if (dreamJob) { startLearning(); }`}</div>
              </div>

              <div className="relative z-10 flex flex-col items-center gap-4">
                <div className="flex flex-col md:flex-row items-center gap-3">
                  <Rocket className="w-6 h-6 text-blue-400 animate-bounce" />
                  <p className="text-xl md:text-2xl font-medium text-blue-100 text-center md:text-left">
                    Ready to{" "}
                    <span className="font-mono text-green-400">crack()</span>{" "}
                    your dream SDE internship?
                  </p>
                </div>
                <div className="px-4 py-2 bg-gray-800/50 rounded-lg font-mono text-sm text-gray-400 border border-gray-700">
                  const success = await applyNow();
                </div>
              </div>
            </div>

            <button
              className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white font-bold rounded-lg shadow-lg hover:shadow-blue-600/20 transition-all duration-300 hover:scale-105 relative group"
              onClick={() => window.open("https://forms.gle/uMpHr5rjz5evyT5K7")}
            >
              <span className="relative z-10">Apply Now</span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SDECurriculum;
